import { useContext, useEffect, useRef } from "react";
import { WindowSizeContext } from "../../../contexts/window_size";
import { useCachedValue } from "../../../hooks/use_cached_value";
import { useCssStateManager } from "../../../hooks/use_css_state_manager";
import { useDesktopScrollbar } from "../../../hooks/use_desktop_scrollbar";
import { useResizeObserver } from "../../../hooks/use_resize_observer";
import { useSmartState } from "../../../hooks/use_smart_state";
import { prebuildData } from "../../../lib/prebuild-data";
import { EventPane } from "../../core/event_pane";
import { Icon } from "../../core/icon";

export type CollectionDropdownMenuProps = {
    open: boolean;
    collectionItems: {handle: string;caption: string}[];
    collectionHandle: string;
    collectionDropdownCloseClickHandler?: () => void;
    collectionDropdownItemClickHandler?: (handle: string) => void;
};

export default function CollectionDropdownMenu({
            open,
            collectionItems,
            collectionHandle,
            collectionDropdownCloseClickHandler = () => {},
            collectionDropdownItemClickHandler = () => {}
        }:CollectionDropdownMenuProps)
{
    const windowSize = useContext(WindowSizeContext);
    const dropdownMenuHeight = useSmartState(0);

    const cssStateManager = useCssStateManager(["container"]);    
    cssStateManager.useProperty(open,"open");
    cssStateManager.useProperty(dropdownMenuHeight.value<=windowSize.height,"dropdown-menu-centered");

    const { ref: scrollerRef,className: scrollerClassName,injectedStyles: scrollerInjectedStyles,styles: scrollerStyles } = useDesktopScrollbar();

    const refDropdownMenuOnResize = useResizeObserver(({height}) => {
        dropdownMenuHeight.value = height;
    });

    const collectionRoutes = useCachedValue(() => {
        const ret: {[handle: string]: string} = {};
        collectionItems.forEach(({handle}) => {
            const collectionItem = prebuildData.getCollectionByHandle(handle);
            ret[handle] = collectionItem.route.split("{handle}").join(handle)            
        });
        return ret;
    },"collections");

    const { mobileCollectionPopupMenu: { backgroundColor,color,highlightColor,hoverColor } } = prebuildData.getActiveTheme();

    return (
        <>
            <div className={cssStateManager.getClassNames()}>                
                <div className={`scroller ${scrollerClassName}`} ref={scrollerRef} style={scrollerStyles} >
                    <div ref={refDropdownMenuOnResize} className="menu">
                        {collectionItems.map(({handle,caption}) => (
                            <div key={handle} className={"item"+((handle===collectionHandle)?" selected":"")}>
                                {caption}
                                <EventPane onClick={() => collectionDropdownItemClickHandler(handle)} seoLinkUrl={collectionRoutes[handle]} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="close-button">
                    <Icon name="navbar-cross" size={20} />
                    <EventPane onClick={collectionDropdownCloseClickHandler} />
                </div>                
            </div>
            <style jsx>{`
            .container {
                position: fixed;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;
                background-color: ${backgroundColor};
                z-index: 104;
                opacity: 0;
                visibility: hidden;
                transform: translateY(150rem);
                --duration: 0.25s;
                transition: opacity var(--duration),visibility var(--duration),transform var(--duration);
            }

            .container.open {
                opacity: 1;
                visibility: visible;
                transform: translateY(0rem);
            }

            .scroller {
                position: absolute;
                left: 0rem;
                top: 0rem;
                width: 100%;
                height: 100%;                
                display: flex;
                flex-direction: column;
                justify-content: start;
                overflow: auto;
            }

            .dropdown-menu-centered .scroller {
                justify-content: center;
                overflow: hidden;
            }

            .scroller .menu {
                padding: 0rem 74rem;
            }

            .scroller .item {
                font-size: 20rem;
                font-weight: 500;
                text-align: center;
                padding: 16rem 0rem;
                color: ${color};
                position: relative;
                --duration: 0.3s;
                transition: background-color var(--duration),color var(--duration);
            }

            .scroller .item.touchdown {
                background-color: rgba(0,0,0,0.02);
            }

            .scroller .item.hover {
                color: ${hoverColor};
            }

            .scroller .item.selected.hover {
                color: ${highlightColor};
            }

            .scroller .item.selected {
                color: ${highlightColor};
                font-weight: 600;
            }

            .close-button {
                position: absolute;
                right: 5rem;
                top: 0rem;
                color: #888;
                padding: 20rem 25rem;
                --duration: 0.3s;
                transition: background-color var(--duration),color var(--duration);
            }

            .close-button.touchdown {
                background-color: rgba(0,0,0,0.06);
            }

            .close-button.hover {
                color: #adadad;                
            }
            `}</style>
        </>
    );
}